import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import en from './translations/en';
import tr from './translations/tr';

const DICTIONARIES = { en, tr };
// Shown in the picker in each language's own name, not translated - a user
// who can't read the current UI language still needs to recognize theirs.
export const LANGUAGE_NAMES = { en: 'English', tr: 'Türkçe' };
const STORAGE_KEY = 'language';

function detectDefaultLanguage() {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && DICTIONARIES[stored]) return stored;
    const browserLang = (navigator.language || 'en').slice(0, 2);
    return DICTIONARIES[browserLang] ? browserLang : 'en';
}

function resolve(dict, key) {
    return key.split('.').reduce((node, part) => (node && typeof node === 'object' ? node[part] : undefined), dict);
}

function interpolate(str, params) {
    if (!params) return str;
    return str.replace(/\{\{(\w+)\}\}/g, (match, name) => (params[name] !== undefined ? params[name] : match));
}

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
    const [language, setLanguageState] = useState(detectDefaultLanguage);

    const setLanguage = useCallback((lang) => {
        if (!DICTIONARIES[lang]) return;
        localStorage.setItem(STORAGE_KEY, lang);
        setLanguageState(lang);
    }, []);

    // Falls back to English for any key missing from a translation, then to
    // the key itself - a page should never render blank just because a
    // string hasn't been translated yet.
    const t = useCallback((key, params) => {
        const value = resolve(DICTIONARIES[language], key) ?? resolve(DICTIONARIES.en, key) ?? key;
        return interpolate(value, params);
    }, [language]);

    const locale = language === 'tr' ? 'tr-TR' : 'en-US';

    const value = useMemo(() => ({
        language,
        setLanguage,
        t,
        locale,
        languages: Object.keys(DICTIONARIES)
    }), [language, setLanguage, t, locale]);

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
    return useContext(LanguageContext);
}
